import React from 'react';
import * as elog from '@reverbdotcom/commons/src/elog';
import { useFiveDigitLocale } from '@reverbdotcom/commons/src/user_five_digit_locale_hook';
import { adyenCheckout } from '@reverbdotcom/commons/src/adyen_checkout_v6';
import type { PaymentAction, CoreConfiguration } from '@adyen/adyen-web-v6';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { SCAState } from './adyen_sca_hook';

interface Props {
  selector: string;
  action: PaymentAction;
  onAdditionalDetails(state: SCAState): void;
  enabled?: boolean;
  environment?: string;
  clientKey?: string;
}

// For 3DS2 configuration see https://docs.adyen.com/online-payments/3d-secure/native-3ds2/web-component
// For 3DS1 redirect configuration see https://docs.adyen.com/online-payments/3d-secure/redirect-3ds2-3ds1/web-component
export function useV6AdyenSCA({ selector, action, onAdditionalDetails, clientKey, environment, enabled = true }: Props) {
  const locale = useFiveDigitLocale();
  const user = useUser();
  const countryCode = user.countryCode || 'US';

  React.useEffect(() => {
    if (!enabled || !environment || !clientKey) {
      return;
    }

    elog.info('use_adyen_sca_action_v6', { action });

    function onError(error) {
      elog.error('use_adyen_sca_error_v6', { actionType: action.type, ...error }, error.errorCode);
    }

    const configuration = {
      clientKey,
      environment: environment as CoreConfiguration['environment'],
      countryCode,
      locale,
      onAdditionalDetails,
      onError,
    };

    const initializeSCA = async () => {
      const checkout = await adyenCheckout({ configuration });

      checkout.createFromAction(action, { size: '05', challengeWindowSize: '05' }).mount(selector);
    };

    initializeSCA();
  }, [locale, selector, environment, clientKey, action, onAdditionalDetails, enabled]);
}
